import { useEffect, useMemo, useState } from "react";
import { Editor } from "@tinymce/tinymce-react";
import "@/lib/tinymce-imports";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useCommunityRole } from "@/hooks/useCommunityRole";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationPrevious, PaginationNext, PaginationEllipsis } from "@/components/ui/pagination";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { Plus, Search, Sparkles, Download, ExternalLink, RefreshCw, Trash2, Pencil, FileText, Info } from "lucide-react";
import { toast } from "sonner";
import { exportGrantLetterPdf } from "@/lib/grants/exportGrantLetterPdf";
import { exportGrantLetterDocx } from "@/lib/grants/exportGrantLetterDocx";

interface Grant {
  id: string;
  pta_id: string;
  name: string;
  funder: string;
  funder_website: string | null;
  amount_requested: number | null;
  amount_awarded: number | null;
  status: string;
  deadline: string | null;
  purpose: string | null;
  eligibility_notes: string | null;
  award_details: string | null;
  application_letter_html: string | null;
  application_letter_ai_draft: string | null;
  application_letter_status: string;
  application_letter_error: string | null;
}

interface AvailableGrant {
  id: string;
  funder: string;
  name: string;
  logo_url: string | null;
  summary: string | null;
  eligibility: string | null;
  amount_min: number | null;
  amount_max: number | null;
  next_deadline: string | null;
  rolling_deadline: boolean;
  website_url: string | null;
}

const STATUS_LABELS: Record<string, string> = {
  researching: "Researching",
  drafting: "Drafting",
  submitted: "Submitted",
  awarded: "Awarded",
  rejected: "Rejected",
  withdrawn: "Withdrawn",
};

const STATUS_COLORS: Record<string, string> = {
  researching: "bg-muted text-muted-foreground",
  drafting: "bg-blue-100 text-blue-800",
  submitted: "bg-amber-100 text-amber-800",
  awarded: "bg-green-100 text-green-800",
  rejected: "bg-red-100 text-red-800",
  withdrawn: "bg-gray-100 text-gray-700",
};

export function GrantsTab({ ptaId }: { ptaId: string }) {
  const { isCommunityRole } = useCommunityRole();
  const { enabled, isSandboxPreview, loading: flagLoading } = useFeatureFlag("community_grants_tab");

  if (flagLoading) return null;
  if (!enabled) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Grants</CardTitle>
          <CardDescription>This feature is being prepared.</CardDescription>
        </CardHeader>
      </Card>
    );
  }
  if (!isCommunityRole && !isSandboxPreview) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Grants</CardTitle>
          <CardDescription>Only members with the Community role can access grants.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const [tab, setTab] = useState("ours");
  const [refreshKey, setRefreshKey] = useState(0);

  const handleGrantAdded = () => {
    setRefreshKey((k) => k + 1);
    setTab("ours");
  };

  return (
    <div className="space-y-4">
      {isSandboxPreview && (
        <div className="rounded-md border border-dashed bg-muted/50 p-3 text-xs text-muted-foreground">
          Sandbox preview — this tab is hidden from real users until Go Live.
        </div>
      )}
      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList>
          <TabsTrigger value="ours">Our Grants</TabsTrigger>
          <TabsTrigger value="available">Available Grants</TabsTrigger>
        </TabsList>
        <TabsContent value="ours" className="space-y-4">
          <OurGrants key={refreshKey} ptaId={ptaId} />
        </TabsContent>
        <TabsContent value="available" className="space-y-4">
          <AvailableGrants ptaId={ptaId} onGrantAdded={handleGrantAdded} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ============================================================
// Our Grants
// ============================================================
function OurGrants({ ptaId }: { ptaId: string }) {
  const [grants, setGrants] = useState<Grant[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Grant | null>(null);
  const [detailGrant, setDetailGrant] = useState<Grant | null>(null);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("grants")
      .select("*")
      .eq("pta_id", ptaId)
      .order("created_at", { ascending: false });
    if (error) {
      toast.error("Could not load grants");
    } else {
      setGrants((data as Grant[]) ?? []);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (ptaId) load();
  }, [ptaId]);

  const filtered = useMemo(() => {
    return grants.filter((g) => {
      if (statusFilter !== "all" && g.status !== statusFilter) return false;
      if (search) {
        const s = search.toLowerCase();
        return g.name.toLowerCase().includes(s) || g.funder.toLowerCase().includes(s);
      }
      return true;
    });
  }, [grants, search, statusFilter]);

  const stats = useMemo(() => {
    const requested = grants.filter((g) => g.status === "submitted" || g.status === "drafting").reduce((s, g) => s + (Number(g.amount_requested) || 0), 0);
    const awarded = grants.filter((g) => g.status === "awarded").reduce((s, g) => s + (Number(g.amount_awarded ?? g.amount_requested) || 0), 0);
    const upcoming = grants.filter((g) => g.deadline && new Date(g.deadline) >= new Date() && g.status !== "awarded" && g.status !== "rejected").length;
    return { requested, awarded, upcoming };
  }, [grants]);

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this grant? Linked income (if awarded) will be removed.")) return;
    const { error } = await supabase.from("grants").delete().eq("id", id);
    if (error) toast.error(error.message);
    else {
      toast.success("Grant deleted");
      load();
    }
  };

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <StatCard label="Requested (open)" value={`£${stats.requested.toFixed(2)}`} />
        <StatCard label="Awarded (YTD)" value={`£${stats.awarded.toFixed(2)}`} />
        <StatCard label="Upcoming deadlines" value={String(stats.upcoming)} />
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4 flex-wrap">
          <div>
            <CardTitle>Our Grants</CardTitle>
            <CardDescription>Track every grant application from research to award.</CardDescription>
          </div>
          <Button onClick={() => { setEditing(null); setFormOpen(true); }}>
            <Plus className="h-4 w-4 mr-1" /> New grant
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search grant or funder…" className="pl-9" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <p className="text-sm text-muted-foreground py-8 text-center">Loading…</p>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">
              No grants yet. Click "New grant" to add one — then open the grant to draft your application letter.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Grant</TableHead>
                  <TableHead>Funder</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Deadline</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Letter</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((g) => (
                  <TableRow key={g.id}>
                    <TableCell className="font-medium">{g.name}</TableCell>
                    <TableCell>{g.funder}</TableCell>
                    <TableCell>{g.amount_awarded ? `£${Number(g.amount_awarded).toFixed(2)}` : g.amount_requested ? `£${Number(g.amount_requested).toFixed(2)}` : "—"}</TableCell>
                    <TableCell>{g.deadline ? new Date(g.deadline).toLocaleDateString("en-GB") : "—"}</TableCell>
                    <TableCell><Badge className={STATUS_COLORS[g.status]}>{STATUS_LABELS[g.status]}</Badge></TableCell>
                    <TableCell>
                      <Badge variant="outline">{g.application_letter_status === "ready" ? "Ready" : g.application_letter_status === "generating" ? "Drafting…" : g.application_letter_status === "failed" ? "Failed" : "Pending"}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <TooltipProvider delayDuration={150}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button variant="ghost" size="icon" onClick={() => setDetailGrant(g)} aria-label="Open application letter">
                              <FileText className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Open application letter</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button variant="ghost" size="icon" onClick={() => { setEditing(g); setFormOpen(true); }} aria-label="Edit grant">
                              <Pencil className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Edit grant</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button variant="ghost" size="icon" onClick={() => handleDelete(g.id)} aria-label="Delete grant">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Delete grant</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <GrantFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        ptaId={ptaId}
        grant={editing}
        onSaved={(g, isNew) => { load(); setFormOpen(false); if (isNew) setDetailGrant(g); }}
      />

      {detailGrant && (
        <GrantDetailDialog
          grant={detailGrant}
          ptaId={ptaId}
          onClose={() => setDetailGrant(null)}
          onUpdated={(g) => { setDetailGrant(g); load(); }}
        />
      )}
    </>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className="text-2xl font-bold mt-1">{value}</p>
      </CardContent>
    </Card>
  );
}

// ============================================================
// Grant form
// ============================================================
function GrantFormDialog({
  open, onOpenChange, ptaId, grant, onSaved,
}: { open: boolean; onOpenChange: (v: boolean) => void; ptaId: string; grant: Grant | null; onSaved: (g: Grant, isNew: boolean) => void }) {
  const { user } = useAuth();
  const [form, setForm] = useState<any>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(grant ?? {
      name: "", funder: "", funder_website: "", amount_requested: "", amount_awarded: "",
      status: "researching", deadline: "", purpose: "", eligibility_notes: "", award_details: "",
    });
  }, [grant, open]);

  const submit = async () => {
    if (!form.name || !form.funder) {
      toast.error("Name and funder are required");
      return;
    }
    if (!ptaId) {
      toast.error("PTA not loaded yet — please retry in a moment.");
      return;
    }
    setSaving(true);
    const payload = {
      pta_id: ptaId,
      name: form.name,
      funder: form.funder,
      funder_website: form.funder_website || null,
      amount_requested: form.amount_requested === "" || form.amount_requested == null ? null : Number(form.amount_requested),
      amount_awarded: form.amount_awarded === "" || form.amount_awarded == null ? null : Number(form.amount_awarded),
      status: form.status,
      deadline: form.deadline || null,
      purpose: form.purpose || null,
      eligibility_notes: form.eligibility_notes || null,
      award_details: form.award_details || null,
      created_by: grant?.id ? undefined : (user?.id ?? null),
    };

    if (grant?.id) {
      const { data, error } = await supabase.from("grants").update(payload).eq("id", grant.id).select().maybeSingle();
      setSaving(false);
      if (error) return toast.error(error.message);
      toast.success("Grant updated");
      onSaved(data as Grant, false);
    } else {
      const { data, error } = await supabase.from("grants").insert(payload).select().maybeSingle();
      setSaving(false);
      if (error) return toast.error(error.message);
      toast.success("Grant created — drafting letter…");
      // Fire-and-forget letter generation
      supabase.functions.invoke("generate-grant-letter", { body: { grant_id: (data as Grant).id } }).catch(() => {});
      onSaved(data as Grant, true);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{grant ? "Edit grant" : "New grant"}</DialogTitle>
          <DialogDescription>{grant ? "Update grant details." : "We'll auto-draft an application letter you can edit."}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Grant name *</Label><Input value={form.name ?? ""} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div><Label>Funder *</Label><Input value={form.funder ?? ""} onChange={(e) => setForm({ ...form, funder: e.target.value })} /></div>
          <div><Label>Funder website</Label><Input value={form.funder_website ?? ""} onChange={(e) => setForm({ ...form, funder_website: e.target.value })} placeholder="https://…" /></div>
          <div><Label>Amount requested (£)</Label><Input type="number" step="0.01" value={form.amount_requested ?? ""} onChange={(e) => setForm({ ...form, amount_requested: e.target.value })} /></div>
          <div><Label>Amount awarded (£)</Label><Input type="number" step="0.01" value={form.amount_awarded ?? ""} onChange={(e) => setForm({ ...form, amount_awarded: e.target.value })} /></div>
          <div>
            <Label>Status</Label>
            <Select value={form.status ?? "researching"} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Deadline</Label><Input type="date" value={form.deadline ?? ""} onChange={(e) => setForm({ ...form, deadline: e.target.value })} /></div>
          <div className="col-span-2"><Label>Purpose / what funds will be used for</Label><Textarea rows={3} value={form.purpose ?? ""} onChange={(e) => setForm({ ...form, purpose: e.target.value })} /></div>
          <div className="col-span-2"><Label>Eligibility & criteria notes</Label><Textarea rows={3} value={form.eligibility_notes ?? ""} onChange={(e) => setForm({ ...form, eligibility_notes: e.target.value })} /></div>
          {form.status === "awarded" && (
            <div className="col-span-2"><Label>Award details</Label><Textarea rows={2} value={form.award_details ?? ""} onChange={(e) => setForm({ ...form, award_details: e.target.value })} /></div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving}>{saving ? "Saving…" : grant ? "Save" : "Create"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// Grant detail (with letter editor)
// ============================================================
function GrantDetailDialog({
  grant: initialGrant, ptaId, onClose, onUpdated,
}: { grant: Grant; ptaId: string; onClose: () => void; onUpdated: (g: Grant) => void }) {
  const [grant, setGrant] = useState<Grant>(initialGrant);
  const [content, setContent] = useState(initialGrant.application_letter_html ?? "");
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(grant.application_letter_status === "generating");
  const [ptaInfo, setPtaInfo] = useState<{ name: string; logo_url: string | null } | null>(null);

  useEffect(() => {
    supabase.from("ptas").select("name,logo_url").eq("id", ptaId).maybeSingle().then(({ data }) => setPtaInfo(data as any));
  }, [ptaId]);

  // Poll for generation completion
  useEffect(() => {
    if (grant.application_letter_status !== "generating") return;
    const t = setInterval(async () => {
      const { data } = await supabase.from("grants").select("*").eq("id", grant.id).maybeSingle();
      if (data && (data as Grant).application_letter_status !== "generating") {
        const g = data as Grant;
        setGrant(g);
        setGenerating(false);
        onUpdated(g);
        if (g.application_letter_status === "failed") {
          toast.error(g.application_letter_error || "Letter generation failed");
        } else if (g.application_letter_status === "ready" && g.application_letter_html?.trim()) {
          setContent(g.application_letter_html);
          toast.success("Letter ready");
        } else {
          toast.error(g.application_letter_error || "Letter generation returned no content");
        }
      }
    }, 3000);
    return () => clearInterval(t);
  }, [grant.id, grant.application_letter_status]);

  const regenerate = async () => {
    setGenerating(true);
    const { data, error } = await supabase.functions.invoke("generate-grant-letter", { body: { grant_id: grant.id } });
    if (error || (data as any)?.error) {
      setGenerating(false);
      toast.error(error?.message || (data as any)?.error || "Failed to start letter generation");
      return;
    }
    // Only now flip status so the poller starts
    setGrant({ ...grant, application_letter_status: "generating" });
  };

  const saveLetter = async () => {
    setSaving(true);
    const { data, error } = await supabase
      .from("grants")
      .update({ application_letter_html: content })
      .eq("id", grant.id)
      .select()
      .maybeSingle();
    setSaving(false);
    if (error) return toast.error(error.message);
    toast.success("Letter saved");
    setGrant(data as Grant);
    onUpdated(data as Grant);
  };

  const resetToDraft = () => {
    if (!grant.application_letter_ai_draft) return;
    if (!confirm("Replace the current letter with the original AI draft?")) return;
    setContent(grant.application_letter_ai_draft);
  };

  const doExport = async (kind: "pdf" | "docx") => {
    if (!content.trim()) { toast.error("Letter is empty"); return; }
    try {
      const args = {
        html: content, grantName: grant.name, funder: grant.funder,
        ptaName: ptaInfo?.name ?? "PTA", ptaLogoUrl: ptaInfo?.logo_url ?? null,
      };
      if (kind === "pdf") await exportGrantLetterPdf(args);
      else await exportGrantLetterDocx(args);
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileText className="h-5 w-5" /> {grant.name}</DialogTitle>
          <DialogDescription>{grant.funder} — application letter</DialogDescription>
        </DialogHeader>

        {grant.status === "awarded" && (
          <div className="flex items-start gap-2 rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-900">
            <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <p>Please let the treasurer know the grant has been awarded and to pass on any payment details to the funder.</p>
          </div>
        )}

        <div className="flex flex-wrap gap-2 mb-3">
          <Button variant="outline" size="sm" onClick={regenerate} disabled={generating}>
            <Sparkles className="h-4 w-4 mr-1" /> {generating ? "Generating…" : grant.application_letter_html ? "Regenerate" : "Generate letter"}
          </Button>
          {grant.application_letter_ai_draft && grant.application_letter_ai_draft !== content && (
            <Button variant="ghost" size="sm" onClick={resetToDraft}><RefreshCw className="h-4 w-4 mr-1" /> Reset to AI draft</Button>
          )}
          <div className="flex-1" />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm"><Download className="h-4 w-4 mr-1" /> Download</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => doExport("pdf")}>PDF</DropdownMenuItem>
              <DropdownMenuItem onClick={() => doExport("docx")}>Word (.docx)</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button size="sm" onClick={saveLetter} disabled={saving}>{saving ? "Saving…" : "Save letter"}</Button>
        </div>

        {generating ? (
          <div className="rounded-md border p-8 text-center text-sm text-muted-foreground">
            <Sparkles className="h-6 w-6 mx-auto mb-2 animate-pulse" />
            Drafting your application letter — this usually takes about 10 seconds.
          </div>
        ) : (
          <Editor
            value={content}
            onEditorChange={setContent}
            licenseKey="gpl"
            init={{
              height: 500,
              menubar: "edit insert view format table tools",
              plugins: "advlist autolink lists link table code fullscreen searchreplace charmap preview wordcount",
              toolbar: "undo redo | blocks | bold italic underline | bullist numlist | link | removeformat | code fullscreen",
              skin: false,
              content_css: false,
              branding: false,
              promotion: false,
              content_style: 'body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; font-size: 14px; padding: 12px; line-height: 1.55; }',
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// Available Grants
// ============================================================
function AvailableGrants({ ptaId, onGrantAdded }: { ptaId: string; onGrantAdded?: () => void }) {
  const { user } = useAuth();
  const [items, setItems] = useState<AvailableGrant[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [adding, setAdding] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;

  useEffect(() => {
    supabase
      .from("available_grants")
      .select("*")
      .eq("is_active", true)
      .order("next_deadline", { ascending: true, nullsFirst: false })
      .then(({ data, error }) => {
        if (error) toast.error("Could not load available grants");
        else setItems((data as AvailableGrant[]) ?? []);
        setLoading(false);
      });
  }, []);

  const filtered = useMemo(() => {
    if (!search) return items;
    const s = search.toLowerCase();
    return items.filter((i) => i.funder.toLowerCase().includes(s) || i.name.toLowerCase().includes(s) || (i.summary ?? "").toLowerCase().includes(s));
  }, [items, search]);

  useEffect(() => { setPage(1); }, [search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const addToPipeline = async (g: AvailableGrant) => {
    if (!ptaId) {
      toast.error("PTA not loaded yet — please retry in a moment.");
      return;
    }
    setAdding(g.id);
    const { data: inserted, error } = await supabase.from("grants").insert({
      pta_id: ptaId,
      name: g.name,
      funder: g.funder,
      funder_website: g.website_url,
      amount_requested: g.amount_max ?? g.amount_min ?? null,
      deadline: g.next_deadline,
      eligibility_notes: g.eligibility,
      purpose: g.summary,
      available_grant_id: g.id,
      status: "researching",
      created_by: user?.id ?? null,
    }).select("id").single();
    setAdding(null);
    if (error) {
      console.error("Add to Our Grants failed", error);
      toast.error(error.message || "Could not add grant");
      return;
    }
    toast.success(`${g.name} added to Our Grants — drafting letter…`);
    if (inserted?.id) {
      supabase.functions.invoke("generate-grant-letter", { body: { grant_id: inserted.id } }).catch(() => {});
    }
    onGrantAdded?.();
  };

  const pageNumbers = useMemo(() => {
    const pages: (number | "ellipsis")[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push("ellipsis");
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      if (currentPage < totalPages - 2) pages.push("ellipsis");
      pages.push(totalPages);
    }
    return pages;
  }, [totalPages, currentPage]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>UK Grants for PTAs</CardTitle>
        <CardDescription>Curated funders for school PTAs. Click "Add to Our Grants" to start an application — we'll auto-draft the letter.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search funder or grant…" className="pl-9" />
        </div>
        {loading ? (
          <p className="text-sm text-muted-foreground py-8 text-center">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground py-8 text-center">No grants yet. The PTA Hub team is curating this list — check back soon.</p>
        ) : (
          <>
            <div className="space-y-3">
              {paged.map((g) => (
                <div
                  key={g.id}
                  className="group flex flex-col sm:flex-row sm:items-center gap-4 rounded-lg border border-border bg-card p-4 transition-all hover:shadow-md hover:border-primary/30 hover:-translate-y-0.5"
                >
                  <div className="flex-shrink-1">
                    {g.logo_url ? (
                      <img src={g.logo_url} alt={g.funder} className="h-24 w-24 object-contain rounded bg-muted/30 p-1" />
                    ) : (
                      <div className="h-24 w-24 rounded bg-muted flex items-center justify-center text-muted-foreground text-sm font-semibold">
                        {g.funder.slice(0, 2).toUpperCase()}
                      </div>
                    )}
                  </div>

                  <div className="flex-1 min-w-0 space-y-1.5">
                    <div>
                      <h3 className="font-semibold text-base leading-tight">{g.name}</h3>
                      <p className="text-sm text-muted-foreground">{g.funder}</p>
                    </div>
                    {g.summary && <p className="text-sm text-muted-foreground line-clamp-2">{g.summary}</p>}
                    <div className="flex flex-wrap gap-2 text-xs pt-1">
                      {(g.amount_min || g.amount_max) && (
                        <Badge variant="outline">
                          £{Number(g.amount_min ?? 0).toLocaleString()}{g.amount_max ? `–£${Number(g.amount_max).toLocaleString()}` : "+"}
                        </Badge>
                      )}
                      {g.rolling_deadline ? (
                        <Badge variant="outline">Rolling deadline</Badge>
                      ) : g.next_deadline ? (
                        <Badge variant="outline">By {new Date(g.next_deadline).toLocaleDateString("en-GB")}</Badge>
                      ) : null}
                    </div>
                  </div>

                  <div className="flex sm:flex-col gap-2 sm:w-44 sm:flex-shrink-0">
                    {g.website_url && (
                      <Button variant="outline" size="sm" asChild className="flex-1 sm:flex-none">
                        <a href={g.website_url} target="_blank" rel="noopener noreferrer"><ExternalLink className="h-3 w-3 mr-1" /> Visit funder</a>
                      </Button>
                    )}
                    <Button size="sm" className="flex-1 sm:flex-none" onClick={() => addToPipeline(g)} disabled={adding === g.id}>
                      <Plus className="h-3 w-3 mr-1" /> {adding === g.id ? "Adding…" : "Add to Our Grants"}
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {totalPages > 1 && (
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      href="#"
                      onClick={(e) => { e.preventDefault(); if (currentPage > 1) setPage(currentPage - 1); }}
                      className={currentPage === 1 ? "pointer-events-none opacity-50" : ""}
                    />
                  </PaginationItem>
                  {pageNumbers.map((p, idx) => (
                    <PaginationItem key={idx}>
                      {p === "ellipsis" ? (
                        <PaginationEllipsis />
                      ) : (
                        <PaginationLink
                          href="#"
                          isActive={p === currentPage}
                          onClick={(e) => { e.preventDefault(); setPage(p); }}
                        >
                          {p}
                        </PaginationLink>
                      )}
                    </PaginationItem>
                  ))}
                  <PaginationItem>
                    <PaginationNext
                      href="#"
                      onClick={(e) => { e.preventDefault(); if (currentPage < totalPages) setPage(currentPage + 1); }}
                      className={currentPage === totalPages ? "pointer-events-none opacity-50" : ""}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
